import { integrationDesignMode } from "./design-mode";
import React from "react";
import { createRoot, type Root } from "react-dom/client";
import {
  FormConfigurationEditor,
  authoringStyles,
  type FormConfigurationEditorProps,
} from "@proof/authoring";
import {
  FormConfigurationSession,
  type FormConfiguration,
} from "@proof/legacy";

const Base =
  typeof HTMLElement === "undefined"
    ? (class {} as typeof HTMLElement)
    : HTMLElement;
/** Standalone host-controlled UI. No API client or project repository is created. */
export class ProofFormConfigurationElement extends Base {
  private root?: Root;
  private input?: unknown;
  private current = new FormConfigurationSession().value;
  private settings: Pick<
    FormConfigurationEditorProps,
    "projectId" | "services"
  > = {};
  private save?: FormConfigurationEditorProps["onSave"];
  private epoch = 0;
  connectedCallback() {
    if (!this.shadowRoot) {
      const shadow = this.attachShadow({ mode: "open" }),
        style = document.createElement("style"),
        mount = document.createElement("div");
      style.textContent = authoringStyles;
      mount.dataset.formConfigurationMount = "";
      mount.style.height = "100%";
      shadow.append(style, mount);
    }
    this.root ||= createRoot(
      this.shadowRoot!.querySelector<HTMLDivElement>(
        "[data-form-configuration-mount]",
      )!,
    );
    this.render();
  }
  disconnectedCallback() {
    this.epoch++;
    this.root?.unmount();
    this.root = undefined;
  }
  set value(value: unknown) {
    const session = new FormConfigurationSession(value, this.settings);
    this.input = structuredClone(value);
    this.current = session.value;
    this.epoch++;
    this.render();
  }
  get value(): FormConfiguration {
    return structuredClone(this.current);
  }
  set options(value: typeof this.settings) {
    const settings = { ...value };
    const session = new FormConfigurationSession(this.input, settings);
    this.current = session.value;
    this.settings = settings;
    this.epoch++;
    this.render();
  }
  get options() {
    return { ...this.settings };
  }
  set onSave(value: FormConfigurationEditorProps["onSave"]) {
    this.save = value;
    this.render();
  }
  get onSave() {
    return this.save;
  }
  private event(name: string, detail: unknown) {
    this.dispatchEvent(
      new CustomEvent(`proof:${name}`, {
        detail: structuredClone(detail),
        bubbles: true,
        composed: true,
      }),
    );
  }
  private render() {
    integrationDesignMode(this);
    if (!this.root) return;
    this.root.render(
      React.createElement(FormConfigurationEditor, {
        ...this.settings,
        value: this.input,
        onChange: (definition, esm) => {
          this.current = structuredClone(definition);
          this.input = structuredClone(esm);
          this.epoch++;
          this.event("form-configuration-change", {
            definition: this.value,
            esm,
          });
        },
        onSave: async (output) => {
          const epoch = this.epoch;
          await this.save?.(structuredClone(output));
          if (!this.isConnected || epoch !== this.epoch)
            throw new Error(
              "Configuration changed while saving. Review the current source.",
            );
          this.current = structuredClone(output.definition);
          this.event("form-configuration-save", output);
        },
      }),
    );
  }
}
export function registerProofFormConfiguration() {
  if (
    typeof customElements !== "undefined" &&
    !customElements.get("proof-form-configuration")
  )
    customElements.define(
      "proof-form-configuration",
      ProofFormConfigurationElement,
    );
}
